import {
  DashboardSquare01Icon,
  CodesandboxIcon,
  UserMultiple03Icon,
  City02Icon,
  UserGroupIcon,
  Car04Icon,
  User03Icon,
} from '@hugeicons/core-free-icons';
import { NavItem } from 'svnxii-ng-shared/components';

const organizationScopedBaselineKeys = new Set([
  'AgencyManager',
  'AgenciesManager',
  'WorkshopMaintenanceManager',
  'ChargingStationsManager',
  'SalesCommercialManager',
  'WarehouseOperationsManager',
]);
const hiddenForOrganizationScopedManagers = (baselineKey: string | undefined) =>
  !baselineKey || !organizationScopedBaselineKeys.has(baselineKey);

const visibleForNonOrganizationScopedManagers = (baselineKey: string | undefined) =>
  !baselineKey || !organizationScopedBaselineKeys.has(baselineKey);

const visibleForNonOrganizationScopedManagersOrSalesCommercialOrWarehouseManager = (
  baselineKey: string | undefined,
) =>
  !baselineKey ||
  !organizationScopedBaselineKeys.has(baselineKey) ||
  baselineKey === 'SalesCommercialManager' ||
  baselineKey === 'WarehouseOperationsManager';

const visibleForNonOrganizationScopedManagersOrWorkshopMaintenanceManager = (
  baselineKey: string | undefined,
) =>
  !baselineKey ||
  !organizationScopedBaselineKeys.has(baselineKey) ||
  baselineKey === 'WorkshopMaintenanceManager';

const visibleForNonOrganizationScopedManagersOrWorkshopMaintenanceOrSalesCommercialManager =
  (baselineKey: string | undefined) =>
    !baselineKey ||
    !organizationScopedBaselineKeys.has(baselineKey) ||
    baselineKey === 'WorkshopMaintenanceManager' ||
    baselineKey === 'SalesCommercialManager' ||
    baselineKey === 'WarehouseOperationsManager';

const hiddenForAgencyManager = (baselineKey: string | undefined) =>
  baselineKey !== 'AgencyManager';

const hiddenForWorkshopChargingSalesCommercialAndWarehouseManagers = (
  baselineKey: string | undefined,
) =>
  baselineKey !== 'WorkshopMaintenanceManager' &&
  baselineKey !== 'ChargingStationsManager' &&
  baselineKey !== 'SalesCommercialManager' &&
  baselineKey !== 'WarehouseOperationsManager';

const hiddenForAgencyChargingSalesCommercialAndWarehouseManagers = (
  baselineKey: string | undefined,
) =>
  baselineKey !== 'AgencyManager' &&
  baselineKey !== 'AgenciesManager' &&
  baselineKey !== 'ChargingStationsManager' &&
  baselineKey !== 'SalesCommercialManager' &&
  baselineKey !== 'WarehouseOperationsManager';

const hiddenForAgencyAndWorkshopManagers = (baselineKey: string | undefined) =>
  baselineKey !== 'AgencyManager' &&
  baselineKey !== 'AgenciesManager' &&
  baselineKey !== 'WorkshopMaintenanceManager' &&
  baselineKey !== 'SalesCommercialManager' &&
  baselineKey !== 'WarehouseOperationsManager';

const hiddenForAgencyWorkshopAndChargingManagers = (baselineKey: string | undefined) =>
  baselineKey !== 'AgencyManager' &&
  baselineKey !== 'WorkshopMaintenanceManager' &&
  baselineKey !== 'ChargingStationsManager';

const hiddenForAgencyWorkshopChargingSalesCommercialAndWarehouseManagers = (
  baselineKey: string | undefined,
) =>
  baselineKey !== 'AgencyManager' &&
  baselineKey !== 'WorkshopMaintenanceManager' &&
  baselineKey !== 'ChargingStationsManager' &&
  baselineKey !== 'SalesCommercialManager' &&
  baselineKey !== 'WarehouseOperationsManager';

const visibleForSystemAdmin = (baselineKey: string | undefined) =>
  baselineKey === 'SystemAdmin';

const visibleForSystemAdminOrWorkshopMaintenanceManager = (
  baselineKey: string | undefined,
) => baselineKey === 'SystemAdmin' || baselineKey === 'WorkshopMaintenanceManager';

export const ORGANIZATION_NAV_ITEMS: NavItem[] = [
  {
    title: 'Home',
    icon: DashboardSquare01Icon,
    route: '/app/organization/home',
    isVisible: hiddenForOrganizationScopedManagers,
  },
  {
    title: 'Organization',
    icon: City02Icon,
    children: [
      {
        title: 'Company',
        icon: City02Icon,
        get route() {
          const companyId = localStorage.getItem('initializedRootCompanyId');
          return companyId ? `/app/organization/company/${companyId}` : undefined;
        },
        isVisible: hiddenForAgencyManager,
      },
      {
        title: 'Departments',
        icon: UserGroupIcon,
        route: '/app/organization/departments',
        isVisible: hiddenForOrganizationScopedManagers,
      },
      // { title: 'Sites', icon: City02Icon, route: '/app/organization/sites' },

      {
        title: 'Agencies',
        icon: UserMultiple03Icon,
        route: '/app/organization/agency',
        isVisible: hiddenForWorkshopChargingSalesCommercialAndWarehouseManagers,
      },
      {
        title: 'Inventories',
        icon: CodesandboxIcon,
        route: '/app/organization/inventory',
        isVisible:
          visibleForNonOrganizationScopedManagersOrSalesCommercialOrWarehouseManager,
      },
      {
        title: 'Workshops',
        icon: CodesandboxIcon,
        route: '/app/organization/workshop',
        isVisible: hiddenForAgencyChargingSalesCommercialAndWarehouseManagers,
      },
      {
        title: 'Charging Stations',
        icon: CodesandboxIcon,
        route: '/app/organization/charging-stations',
        isVisible: hiddenForAgencyAndWorkshopManagers,
      },
      {
        title: 'Employees',
        icon: UserGroupIcon,
        route: '/app/organization/employees',
        isVisible: hiddenForOrganizationScopedManagers,
      },
    ],
  },
  {
    title: 'Car Attributes',
    icon: Car04Icon,
    isVisible: hiddenForAgencyWorkshopAndChargingManagers,
    children: [
      {
        title: 'Parent Company',
        route: '/app/organization/car-attributes/car-parent-company',
      },
      { title: 'Brand', route: '/app/organization/car-attributes/brand' },
      // { title: 'Color', route: '/app/organization/car-attributes/color' },
      { title: 'Fuel Type', route: '/app/organization/car-attributes/fuel-type' },
      {
        title: 'Interior Color',
        route: '/app/organization/car-attributes/interior-color',
      },
      {
        title: 'Exterior Color',
        route: '/app/organization/car-attributes/exterior-color',
      },
      {
        title: 'Car Trims',
        route: '/app/organization/car-attributes/car-trims',
      },
      {
        title: 'Car Upholstery',
        route: '/app/organization/car-attributes/car-upholstery',
      },
      {
        title: 'Drivetrain Types',
        route: '/app/organization/car-attributes/drivetrain-types',
      },
      {
        title: 'Charging Ports',
        route: '/app/organization/car-attributes/charging-ports',
      },
      {
        title: 'Charger Types',
        route: '/app/organization/car-attributes/charger-types',
      },
    ],
  },
  {
    title: 'Customer Options',
    icon: UserMultiple03Icon,
    isVisible: hiddenForAgencyWorkshopChargingSalesCommercialAndWarehouseManagers,
    children: [
      {
        title: 'Personnel Title',
        route: '/app/organization/customers/personnel-title',
      },
      {
        title: 'Personal Relations',
        route: '/app/organization/customers/personal-relations',
      },
      { title: 'Reach Sources', route: '/app/organization/customers/source' },
      { title: 'House Types', route: '/app/organization/customers/house-types' },
    ],
  },

  {
    title: 'Options',
    icon: CodesandboxIcon,
    isVisible:
      visibleForNonOrganizationScopedManagersOrWorkshopMaintenanceOrSalesCommercialManager,
    children: [
      {
        title: 'Currencies',
        route: '/app/organization/options/currencies',
        isVisible: visibleForSystemAdmin,
      },
      {
        title: 'Measure Units',
        route: '/app/organization/options/unit-of-measures',
        isVisible:
          visibleForNonOrganizationScopedManagersOrSalesCommercialOrWarehouseManager,
      },
      {
        title: 'Workshop Services',
        route: '/app/organization/options/workshop-services',
        isVisible: visibleForSystemAdminOrWorkshopMaintenanceManager,
      },
      {
        title: 'Geo',
        route: '/app/organization/options/geo',
        isVisible: visibleForSystemAdmin,
      },
      {
        title: 'Settings',
        route: '/app/organization/options/settings',
        isVisible: visibleForSystemAdmin,
      },
    ],
  },
  {
    title: 'Account',
    devFeature: true,
    icon: User03Icon,
    route: '/app/organization/notifications',
    isVisible: hiddenForOrganizationScopedManagers,
  },
];
